import { useRouter } from '@tanstack/react-router';
import {
  Icon,
  Image,
  InlineStack,
  Link,
  List,
  ListItem,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
} from '@customerio/pluma-components/react';
import redshiftLogo from '@/assets/images/integrations/redshift.svg';
import bigqueryLogo from '@/assets/images/integrations/bigquery.svg';
import snowflakeLogo from '@/assets/images/integrations/snowflake.svg';

const CONNECTOR_HREFS = {
  redshift: '/workspaces/$workspaceId/integrations/warehouse/redshift',
  bigquery: '/workspaces/$workspaceId/integrations/warehouse/bigquery',
  snowflake: '/workspaces/$workspaceId/integrations/warehouse/snowflake',
} as const;

const CONNECTORS = [
  { slug: 'redshift', name: 'Amazon Redshift', logo: redshiftLogo },
  { slug: 'bigquery', name: 'Google BigQuery', logo: bigqueryLogo },
  { slug: 'snowflake', name: 'Snowflake', logo: snowflakeLogo },
] as const;

type TipsAndResourcesProps = {
  workspaceId: string;
  destinationName: string;
};

/** Replaces the upgrade panel once a sync is already on the latest schema. */
export function TipsAndResources({ workspaceId, destinationName }: TipsAndResourcesProps) {
  const router = useRouter();

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle level="3">
          <InlineStack alignBlock="center" gap="050">
            <Icon name="tip" size="sm" color="base" />
            Tips &amp; resources
          </InlineStack>
        </PanelTitle>
      </PanelHeader>
      <PanelSection display="flex" flexDirection="column" gap="100">
        <Paragraph variant="secondary" sizeVariant="xs" mb="200">
          Learn how to set up your data warehouse to automatically ingest this data from your {destinationName} bucket.
        </Paragraph>

        <List styleType="none" gap="100" mb="200">
          {CONNECTORS.map((connector) => (
            <ListItem key={connector.slug}>
              <Link
                variant="secondary"
                isUnderlined={false}
                icon="chevron-right"
                iconPosition="trailing"
                href={router.buildLocation({ to: CONNECTOR_HREFS[connector.slug], params: { workspaceId } }).href}
              >
                <Image src={connector.logo} alt="" size="icon-v2-sm" display="inline-block" verticalAlign="middle" />
                {connector.name}
              </Link>
            </ListItem>
          ))}
        </List>

        <Paragraph variant="secondary" sizeVariant="xs">
          Learn more about{' '}
          <Link href="https://docs.customer.io/data-warehouse-sync" variant="secondary" isExternal={true}>
            syncing to a data warehouse
          </Link>
        </Paragraph>
      </PanelSection>
    </Panel>
  );
}
